import { Button, getChannel, Input, InputMaxPasswordLength, Tooltip, useTheme } from '@ytl/common-web';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as InfoIcon } from '../../../../assets/info.svg';
import { useApp } from '../../../../contexts/appContext';
import { selectCurrentUser } from '../../../../contexts/currentUserSlicer';
import { useLanguage } from '../../../../contexts/languageContext';
import useRecaptcha from '../../../../modules/useRecaptcha';
import {
  getAnalytics,
  InitParams,
  StepperControls,
  VerificationPhase,
} from '../../../../modules/verificationComponent/VerificationComponent';
import { WidgetEvent } from '../../../@types/widgetEvents';
import { verificatorResource } from '../../../resource/verificator.resource';
import { isValidEmail } from '../../../utils/emailValidator';
import { emitProcessPhase, emitWidgetEvent } from '../../../utils/emitData';
import { usePassword } from '../../../utils/usePassword';
import { RecaptchaPortal } from '../../RecaptchaPortal';

type GetEmailProps = {
  stepper: StepperControls;
  initParams?: InitParams | null;
  showLoginOnActive?: boolean;
  email: string;
  sessionId?: string;
  setEmail: (email: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isFinished: boolean;
  emailAgain?: boolean;
  corrId?: string;
  enableAnalytics?: boolean;
  selectedCA?: string;
};

export const GetEmail = ({
  stepper,
  initParams,
  email,
  sessionId,
  setEmail,
  isLoading,
  setIsLoading,
  isFinished,
  emailAgain = false,
  corrId,
  enableAnalytics = false,
  selectedCA,
  showLoginOnActive = false,
}: GetEmailProps) => {
  const { translation, language } = useLanguage();
  const { isWebView } = useTheme();
  const currentUser = useSelector(selectCurrentUser);

  const borgAnalytics = getAnalytics(enableAnalytics);

  const [validationErrorList, setValidationErrorList] = useState<Array<string>>([]);
  const [emailExists, setEmailExists] = useState<boolean>(false);
  const [tmsData, setTmsData] = useState<any>(null);
  const [accountCategory, setAccountCategory] = useState<string | undefined>(undefined);
  const waitingForCaptchaRef = useRef(false);
  const [emailConfirm, setEmailConfirm] = useState('');
  const [emailConfirmErrorList, setEmailConfirmErrorList] = useState<Array<string>>([]);
  const {
    recaptchaRef,
    recaptchaForceRender,
    recaptchaLang,
    handleRecaptcha,
    captchaToken,
    recaptchaSiteKey,
    recaptchaInvisible,
    executeInvisibleCaptcha,
    needsCaptcha,
  } = useRecaptcha();
  const { isWidgetMode } = useApp();
  const showCaptcha = isWidgetMode ? initParams?.captchaNeeded : needsCaptcha;
  const showLoginButton = initParams?.showLoginButton ?? true;
  const {
    password,
    setPassword,
    passwordAgain,
    setPasswordAgain,
    validatePasswords,
    hasPasswordError,
    passwordsAreNotMatching,
    passwordErrors,
  } = usePassword();

  useEffect(() => {
    emitProcessPhase('GetEmail');
  }, []);

  const continueClicked = useCallback(async () => {
    setTmsData(null);
    setEmailExists(false);
    if (!isValidEmail(email)) {
      setValidationErrorList([translation.emailChange.error.invalidEmail]);
      emitWidgetEvent(
        {
          eventName: WidgetEvent.EmailPrechecked,
          eventValue: { email: email, reason: 'invalid' },
          isSuccess: false,
        },
        { sessionId: sessionId || '' },
      );
      return;
    }

    // Validate email confirmation if emailAgain is enabled
    if (emailAgain) {
      if (email !== emailConfirm) {
        setEmailConfirmErrorList([translation.emailChange.error.emailMismatch]);
        return;
      } else {
        setEmailConfirmErrorList([]);
      }
    }

    // Validate password if password is enabled
    if (initParams?.withPassword && !validatePasswords()) {
      return;
    }

    // If invisible captcha is enabled and no token yet, trigger captcha execution
    if (showCaptcha && recaptchaInvisible && !captchaToken) {
      waitingForCaptchaRef.current = true;
      await executeInvisibleCaptcha();
      return; // Wait for captcha token via effect
    }

    if (showCaptcha && !captchaToken) {
      setValidationErrorList([translation.yeppRegistration.webcomponent.captchaRequired]);
      return;
    }

    waitingForCaptchaRef.current = false;
    setValidationErrorList([]);
    setIsLoading(true);
    try {
      // Mock response for testing YTV flow - replace with actual API call
      /* const verifyResponseEmail = await Promise.resolve({
        status: 200,
        statusText: 'OK',
        data: {
          status: 'used',
          message: 'Email is in use',
          tmsInfo: {
            accountId: 'f38995a3-fc71-45c4-9b76-660477805ac1',
            accountCategory: 'YTV',
            accountState: 'active',
            tvInfo: {
              relatedMsisdn: '36201500381',
              relatedCA: '6123226233',
            },
          },
        },
      }); */
      // Actual API call (commented out for testing):
      const verifyResponseEmail = await verificatorResource.validateEmail({ sessionId, email, password }, corrId);

      // Check for HTTP error status codes (4xx and 5xx)
      if (verifyResponseEmail.status >= 400) {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'validateEmail',
          action: 'TechnicalError',
          result: 'false',
          reason: `HTTP Error ${verifyResponseEmail.status}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'validateEmail',
              details: `HTTP Error ${verifyResponseEmail.status}: ${verifyResponseEmail.statusText}`,
            },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        return;
      }

      // Check for application-level errors in response data
      if (verifyResponseEmail.data.status === 'error') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'ValidateEmail',
          action: 'TechnicalError',
          result: 'false',
          reason: `${verifyResponseEmail.data}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'validateEmail',
              details: verifyResponseEmail.data,
            },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        return;
      }

      if (verifyResponseEmail.data.status === 'invalid') {
        setValidationErrorList([translation.yeppRegistration.webcomponent.invalidEmail]);
        emitWidgetEvent(
          {
            eventName: WidgetEvent.EmailPrechecked,
            eventValue: { email: email, reason: verifyResponseEmail.data.status },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        return;
      }

      if (verifyResponseEmail.data.status === 'used' || verifyResponseEmail.data.status === 'inuse') {
        // Special case: If account is YTV with matching CA, continue
        const customerAccountId = currentUser?.assignedCustomers?.[0]?.customerAccountId;

        if (selectedCA || customerAccountId) {
          const details = verifyResponseEmail.data.tmsInfo;
          const tvInfo = details?.tvInfo;
          const caToCompare = selectedCA || customerAccountId;
          setAccountCategory(details?.accountCategory);

          if (details?.accountCategory === 'YTV' && tvInfo?.relatedCA === caToCompare) {
            // CA matches - continue with the flow instead of showing error
            emitWidgetEvent(
              {
                eventName: WidgetEvent.EmailPrechecked,
                eventValue: { email: email, reason: 'YTV account with matching CA' },
                isSuccess: true,
              },
              { sessionId: sessionId || '' },
            );
            // Continue to send verification code
          } else {
            setTmsData(verifyResponseEmail.data.tmsInfo);
            setEmailExists(true);
            setAccountCategory(details?.accountCategory);
            setValidationErrorList([translation.yeppRegistration.webcomponent.invalidEmail]);
            emitWidgetEvent(
              {
                eventName: WidgetEvent.EmailPrechecked,
                eventValue: { email: email, reason: 'TMS-MTA exist', tmsdata: verifyResponseEmail.data.tmsInfo },
                isSuccess: false,
              },
              { sessionId: sessionId || '' },
            );
            setIsLoading(false);
            return;
          }
        } else {
          setTmsData(verifyResponseEmail.data.tmsInfo);
          setEmailExists(true);
          setAccountCategory(verifyResponseEmail.data.tmsInfo?.accountCategory);
          setValidationErrorList([translation.yeppRegistration.webcomponent.invalidEmail]);
          emitWidgetEvent(
            {
              eventName: WidgetEvent.EmailPrechecked,
              eventValue: { email: email, reason: 'TMS-MTA exist', tmsdata: verifyResponseEmail.data.tmsInfo },
              isSuccess: false,
            },
            { sessionId: sessionId || '' },
          );
          setIsLoading(false);
          return;
        }
      }

      if (verifyResponseEmail.data.status === 'banned') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Temporary termination email',
          action: 'ProcessFinished',
          result: 'false',
          reason: 'banned',
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.EmailPrechecked,
            eventValue: { email: email, reason: verifyResponseEmail.data.status },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        stepper.goToPhase(VerificationPhase.Banned);
        return;
      }

      if (verifyResponseEmail.data.status === 'limit_reached') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Temporary termination email',
          action: 'ProcessFinished',
          result: 'false',
          reason: 'banned',
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.EmailPrechecked,
            eventValue: { email: email, reason: verifyResponseEmail.data.status },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        stepper.goToPhase(VerificationPhase.Banned);
        return;
      }

      emitWidgetEvent(
        {
          eventName: WidgetEvent.EmailPrechecked,
          eventValue: { email: email },
          isSuccess: true,
        },
        { sessionId: sessionId || '' },
      );

      const verifyResponseSendCode = await verificatorResource.sendEmailVerificationCode(
        {
          sessionId: sessionId,
          lang: initParams?.language || language,
          clientId: initParams?.clientID,
        },
        corrId,
      );

      // Check for HTTP error status codes (4xx and 5xx)
      if (verifyResponseSendCode.status >= 400) {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'sendEmailVerificationCode',
          action: 'TechnicalError',
          result: 'false',
          reason: `HTTP Error ${verifyResponseSendCode.status}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'sendEmailVerificationCode',
              details: `HTTP Error ${verifyResponseSendCode.status}: ${verifyResponseSendCode.statusText}`,
            },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        return;
      }

      // Check for application-level errors in response data
      if (verifyResponseSendCode.data.status === 'error') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'sendEmailVerificationCode',
          action: 'TechnicalError',
          result: 'false',
          reason: `${verifyResponseSendCode.data}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'sendEmailVerificationCode',
              details: verifyResponseSendCode.data,
            },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        return;
      }

      if (verifyResponseSendCode.data.status === 'banned' || verifyResponseSendCode.data.status === 'limit_reached') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Temporary termination email',
          action: 'ProcessFinished',
          result: 'false',
          reason: 'banned',
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.EmailVerificationCodeReSent,
            eventValue: { reason: verifyResponseSendCode.data.status },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        setIsLoading(false);
        stepper.goToPhase(VerificationPhase.Banned);
        return;
      }

      borgAnalytics.sendRegistrationEvent({
        channel: getChannel(isWebView),
        virtualName: 'Email authentication code',
        action: 'EmailVerificationCodeSent',
        result: 'true',
      });
      emitWidgetEvent(
        {
          eventName: WidgetEvent.EmailVerificationCodeSent,
          eventValue: {},
          isSuccess: true,
        },
        { sessionId: sessionId || '' },
      );

      stepper.goToNext();
    } catch (err) {
      borgAnalytics.sendRegistrationEvent({
        channel: getChannel(isWebView),
        virtualName: 'GetEmail',
        action: 'TechnicalError',
        result: 'false',
        reason: `${err}`,
      });
      emitWidgetEvent(
        {
          eventName: WidgetEvent.TechnicalError,
          eventValue: {
            process: 'GetEmail',
            details: err,
          },
          isSuccess: false,
        },
        { sessionId: sessionId || '' },
      );
      console.error(err);
      setIsLoading(false);
    }
  }, [
    email,
    translation,
    stepper,
    captchaToken,
    sessionId,
    initParams,
    recaptchaInvisible,
    executeInvisibleCaptcha,
    validatePasswords,
    emailAgain,
    emailConfirm,
    language,
    setIsLoading,
    corrId,
    showCaptcha,
    borgAnalytics,
    selectedCA,
    currentUser,
    password,
    isWebView,
  ]);

  const handleLoginClicked = () => {
    emitWidgetEvent(
      {
        eventName: WidgetEvent.EmailPrecheckLogin,
        eventValue: { email: email, reason: 'TMS-MTA exist', tmsdata: tmsData },
        isSuccess: false,
      },
      { sessionId: sessionId || '' },
    );
    setIsLoading(true);
  };

  // Auto-retry when captcha token arrives after invisible captcha execution
  useEffect(() => {
    if (waitingForCaptchaRef.current && captchaToken) {
      waitingForCaptchaRef.current = false;
      continueClicked();
    }
  }, [captchaToken, continueClicked]);

  // Reset emailExists and tmsData when email changes
  useEffect(() => {
    setEmailExists(false);
    setTmsData(null);
    setAccountCategory(undefined);
  }, [email]);

  const getPasswordErrorList = useMemo(() => {
    if (!passwordErrors) {
      return [];
    }
    const errors: string[] = [];
    if (passwordErrors.hasLowerCaseLetter === false) {
      errors.push(translation.password.errors.lowerCase);
    }
    if (passwordErrors.hasUpperCaseLetter === false) {
      errors.push(translation.password.errors.upperCase);
    }
    if (passwordErrors.hasNumber === false) {
      errors.push(translation.password.errors.atLeastANumber);
    }
    if (passwordErrors.hasSpecialCharacter === false) {
      errors.push(translation.password.errors.atLeastASpecial);
    }
    if (passwordErrors.lengthIsCorrect === false) {
      errors.push(translation.password.errors.lengthCheck);
    }
    if (passwordErrors.passwordsAreMatching === false) {
      errors.push(translation.passwordSet.error.doesntMatch);
    }
    return errors;
  }, [passwordErrors, translation]);

  return (
    <div className="verification-step">
      <div className="register-page__container__box__inputs">
        <Input
          label={translation.emailChange.email}
          name={'email'}
          value={email}
          inputMode={'email'}
          required={true}
          placeholder={isWebView ? 'pelda@e-mail.com' : ''}
          onChange={e => {
            setEmail(e.target.value);
            setValidationErrorList([]);
          }}
          errorList={validationErrorList}
          disabled={isLoading || isFinished}
        />
        {emailAgain && (
          <Input
            label={translation.emailChange.emailAgain}
            name={'emailConfirm'}
            value={emailConfirm}
            inputMode={'email'}
            required={true}
            placeholder={isWebView ? 'pelda@e-mail.com' : ''}
            onChange={e => {
              setEmailConfirm(e.target.value);
              setEmailConfirmErrorList([]);
            }}
            errorList={emailConfirmErrorList}
            disabled={false}
          />
        )}
        {((emailExists && accountCategory !== 'DUP' && showLoginButton) ||
          (emailExists && accountCategory === 'DUP' && showLoginOnActive)) && (
          <p style={{ marginBottom: '1rem' }}>
            {translation.yeppRegistration.webcomponent.emailExists}
            <span className="verification-step__email-exists__login-here">
              <Button
                variant={'text'}
                onClick={handleLoginClicked}
                disabled={isLoading || isFinished}
              >
                {translation.yeppRegistration.webcomponent.loginHere}
              </Button>
            </span>
          </p>
        )}
        {initParams?.withPassword && (
          <>
            <Input
              label={
                <>
                  {translation.loginRegister.register.box.password}
                  <Tooltip
                    html={translation.loginRegister.register.box.passwordTooltip}
                    icon={<InfoIcon className={'icon icon--tooltip'} />}
                    includeIconInTooltip={true}
                  />
                </>
              }
              aria-label={translation.loginRegister.register.box.password}
              type={'password'}
              name={'password'}
              value={password}
              required={true}
              placeholder={isWebView ? '************' : ''}
              maxLength={InputMaxPasswordLength}
              onChange={e => setPassword(e.target.value)}
              hasError={hasPasswordError || passwordsAreNotMatching}
              disabled={isLoading || isFinished}
            />

            <Input
              label={
                <>
                  {translation.loginRegister.register.box.passwordAgain}
                  <Tooltip
                    html={translation.loginRegister.register.box.passwordTooltip}
                    icon={<InfoIcon className={'icon icon--tooltip'} />}
                    includeIconInTooltip={true}
                  />
                </>
              }
              aria-label={translation.loginRegister.register.box.passwordAgain}
              type={'password'}
              name={'passwordAgain'}
              value={passwordAgain}
              required={true}
              placeholder={isWebView ? '************' : ''}
              maxLength={InputMaxPasswordLength}
              onChange={e => setPasswordAgain(e.target.value)}
              hasError={hasPasswordError || passwordsAreNotMatching}
              errorList={getPasswordErrorList}
              disabled={isLoading || isFinished}
            />

            <p className="register-page__container__box__password-hint">
              {translation.loginRegister.register.box.passwordHint}
            </p>
          </>
        )}
      </div>

      {showCaptcha && (
        <RecaptchaPortal
          recaptchaRef={recaptchaRef}
          sitekey={recaptchaSiteKey}
          hl={recaptchaLang}
          onChange={handleRecaptcha}
          className={'verification-step__recaptcha'}
          asyncScriptOnLoad={recaptchaForceRender}
          size={recaptchaInvisible ? 'invisible' : undefined}
          themeOverride={initParams?.theme}
          languageOverride={initParams?.language}
        />
      )}

      <div className={`verification-step__actions${isWebView ? ' verification-step__actions--webview' : ''}`}>
        <Button
          variant="contained"
          onClick={continueClicked}
          disabled={
            isLoading ||
            isFinished ||
            email.trim().length === 0 ||
            (emailAgain && emailConfirm.trim().length === 0) ||
            (initParams?.withPassword && password.trim().length === 0)
          }
        >
          {translation.yeppRegistration.webcomponent.continue}
        </Button>
      </div>
    </div>
  );
};
